import {Placement} from '@popperjs/core';
import React, {FC, ReactNode, useCallback, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {AnalyticsDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/analyticsDataEventObject';
import {trackDataEvent} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {InlineLinkButton} from '../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {createSelector} from '../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {DismissedAlertsEnum} from '../../../models/api/preferencesModel';
import {ThunkAction} from '../../../store/appThunk';
import {updateDismissedAlertsAction} from '../../../store/data/preferences/preferencesActions';
import {getDismissedAlerts} from '../../../store/data/preferences/preferencesSelectors';
import {AnalyticsReportsEnum, ReportElementIdsEnum} from '../../helpers/reportHelpers/reportTypes';
import {ViewInInboxDialog} from './viewInInboxDialog';

/*
 * Types.
 */

export enum ViewInInboxLinkPositionsEnum {
  LEFT = 'left',
  RIGHT = 'right',
}

interface ViewInInboxStyleOptions {
  /** Defaults to `LEFT`. */
  linkSide?: ViewInInboxLinkPositionsEnum;

  /**
   * In case the component should be centered, this add a dummy box of equal size on the other side of it.
   * Defaults to `false`.
   */
  shouldAddDummyBox?: boolean;

  /**
   * Placement of the 'View in inbox' tooltip.
   * Defaults to `top-start`.
   */
  tooltipPlacement?: Placement;

  /**
   * Color of the text, when hovered.
   * Defaults to `theme.interactiveText`.
   */
  hoverColor?: string;

  /**
   * Defaults to `false`.
   */
  shouldShowAssignedConfirmationDialog?: boolean;
}

type ViewInInboxTrackingPayload = {
  report_section: ReportElementIdsEnum;

  // The value of the clicked element.
  clicked_value: number | string | undefined;

  resource_type?: 'teammate' | 'inbox' | 'conversation';
  resource_id?: number;
  column?: string;
};

/*
 * Styles.
 */

// Using visibility because we want to keep the icon in the page flow.
const StyledIcon = styled(Icon)`
  visibility: hidden;
`;

const StyledDummyBoxDiv = styled.div`
  width: 16px;
  height: 16px;
`;

const FlexRowDiv = styled.div<{$linkSide: ViewInInboxLinkPositionsEnum}>`
  display: flex;
  flex-direction: ${({$linkSide}) =>
    $linkSide === ViewInInboxLinkPositionsEnum.LEFT ? 'row-reverse' : 'row'};
  justify-content: ${({$linkSide}) => ($linkSide === ViewInInboxLinkPositionsEnum.LEFT ? 'end' : 'start')};
  align-items: center;
  gap: 8px;

  &:hover ${StyledIcon} {
    visibility: visible;
  }
`;

/*
 * Selector.
 */

const getHasDismissedAssignedViewInInboxAlert = createSelector([getDismissedAlerts], (dismissedAlerts) =>
  dismissedAlerts.includes(DismissedAlertsEnum.LIVE_DASHBOARD_ASSIGNED_VIEW_IN_INBOX),
);

/*
 * Component.
 */

interface ViewInInboxProps extends ViewInInboxStyleOptions {
  actionBuilder: () => ThunkAction;
  trackingPayload: ViewInInboxTrackingPayload;
  children?: ReactNode;
}

export const ViewInInbox: FC<ViewInInboxProps> = ({trackingPayload, children, actionBuilder, ...options}) => {
  const linkSide = options.linkSide ?? ViewInInboxLinkPositionsEnum.LEFT;
  const shouldAddDummyBox = options.shouldAddDummyBox ?? false;
  const tooltipPlacement = options.tooltipPlacement ?? 'top-start';
  const shouldShowAssignedConfirmationDialog = options.shouldShowAssignedConfirmationDialog ?? false;

  const dispatch = useAppDispatch();
  const theme = useUserTheme();
  const [shouldShowDialog, setShouldShowDialog] = useState(false);

  const hoverColor = options.hoverColor ?? theme.interactiveText;

  const hasDismissedAlert = useAppSelector(getHasDismissedAssignedViewInInboxAlert);
  const onDismissAlertCheckboxToggle = useCallback(() => {
    dispatch(updateDismissedAlertsAction(DismissedAlertsEnum.LIVE_DASHBOARD_ASSIGNED_VIEW_IN_INBOX));
  }, [dispatch]);

  const navigate = () => {
    trackDataEvent(
      {name: 'analytics', payload: {report_type: AnalyticsReportsEnum.LIVE_DASHBOARD}},
      DataEventActionsEnum.CLICK,
      AnalyticsDataEventObject.VIEW_IN_INBOX,
      trackingPayload,
    );

    dispatch(actionBuilder());
  };

  return (
    <>
      <TooltipCoordinator
        placement={tooltipPlacement}
        render={() => <Trans id="live-dashboard-view-in-inbox">View in inbox</Trans>}
      >
        <InlineLinkButton
          color="inherit"
          hoverColor={hoverColor}
          onClick={() => {
            // If user has dismissed the alert, just perform the redirect.
            if (!shouldShowAssignedConfirmationDialog || hasDismissedAlert) {
              navigate();
            } else {
              setShouldShowDialog(true);
            }
          }}
        >
          <FlexRowDiv $linkSide={linkSide}>
            {shouldAddDummyBox && <StyledDummyBoxDiv />}
            {children}
            <StyledIcon name="externalLinkSmall" />
          </FlexRowDiv>
        </InlineLinkButton>
      </TooltipCoordinator>
      {shouldShowDialog && (
        <ViewInInboxDialog
          hasDismissedAlert={hasDismissedAlert}
          onConfirm={(hasToggledDismissal: boolean) => {
            if (hasToggledDismissal) {
              onDismissAlertCheckboxToggle();
            }

            navigate();
            setShouldShowDialog(false);
          }}
          onCancel={() => {
            setShouldShowDialog(false);
          }}
        />
      )}
    </>
  );
};
